import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { toast } from 'sonner';
import {
  FileCheck2, ArrowRight, Sparkles, RotateCcw, CheckCircle2, IndianRupee,
  Download, FileText, AlertCircle, Loader2, Inbox, ArrowLeft,
} from 'lucide-react';
import { RequiredDocsChecklist } from '../components/RequiredDocsChecklist';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import AdminWizardClaimDetail from '../components/AdminWizardClaimDetail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────
// Admin Grant Claims — Kanban-style pipeline.
//
// Columns are the 5 non-draft statuses. Draft claims (still being prepared
// by the secretary) are intentionally hidden from accounts — they only see
// claims that have been formally submitted.
//
// Click a card → side-sheet with full detail, attachments, AI verdict and
// action buttons. Actions ALWAYS go through `admin_update_claim_status`
// which also fires the secretary's notification email in the background.
// ─────────────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'submitted',         label: 'Submitted',         tone: 'border-sky-300 bg-sky-50/40' },
  { key: 'in_process',        label: 'In Process',        tone: 'border-amber-300 bg-amber-50/40' },
  { key: 'approved',          label: 'Approved',          tone: 'border-emerald-300 bg-emerald-50/40' },
  { key: 'payment_initiated', label: 'Payment Initiated', tone: 'border-blue-300 bg-blue-50/40' },
  { key: 'paid',              label: 'Paid',              tone: 'border-emerald-600 bg-emerald-100/40' },
];

const NEXT_STATUS = {
  submitted: 'in_process',
  in_process: 'approved',
  approved: 'payment_initiated',
  payment_initiated: 'paid',
};

const STATUS_LABEL = {
  draft: 'Draft', submitted: 'Submitted', in_process: 'In Process',
  approved: 'Approved', payment_initiated: 'Payment Initiated', paid: 'Paid',
};

const ScoreBadge = ({ score }) => {
  if (score == null) return null;
  const tone = score >= 80 ? 'bg-emerald-100 text-emerald-800'
    : score >= 50 ? 'bg-amber-100 text-amber-800'
    : 'bg-rose-100 text-rose-800';
  return (
    <Badge className={`${tone} text-[10px] gap-1 px-1.5 py-0`} title="AI satisfaction score">
      <Sparkles className="h-2.5 w-2.5" />
      {score}/100
    </Badge>
  );
};

// 5-step pass count badge for wizard claims (replaces the legacy AI
// satisfaction score that only made sense for the old free-form upload
// flow). A green "5/5 ✓" pill means every step passed AI evaluation.
const WizardStepBadge = ({ claim }) => {
  const v = claim.wizard_step_verdicts || {};
  const lv = claim.letterhead_verdict || {};
  const passes = [
    v.step1?.verdict === 'specific',
    v.step2?.verdict === 'pass',
    v.step3?.verdict === 'pass',
    (lv.verdict || v.step4?.verdict) === 'pass',
    !!claim.resolution_attachment_id,
  ];
  const passed = passes.filter(Boolean).length;
  const tone = passed === 5 ? 'bg-emerald-100 text-emerald-800'
    : passed >= 3 ? 'bg-amber-100 text-amber-800'
    : 'bg-rose-100 text-rose-800';
  return (
    <Badge className={`${tone} text-[10px] gap-1 px-1.5 py-0`} title="Wizard steps passed">
      <Sparkles className="h-2.5 w-2.5" />
      {passed}/5 ✓
    </Badge>
  );
};

const ClaimCard = ({ claim, onClick }) => {
  const isWizard = claim.wizard_state != null;
  const claimedAmount = Number(claim.claim_amount) || 0;
  const approvedAmount = Number(claim.approved_amount) || 0;
  const partial = approvedAmount > 0 && claimedAmount > 0 && approvedAmount < claimedAmount;
  return (
  <button
    onClick={onClick}
    className="w-full text-left rounded-lg border bg-white p-3 hover:shadow-md hover:border-emerald-300 transition space-y-2"
    data-testid={`admin-claim-card-${claim.id}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="font-semibold text-sm leading-tight line-clamp-2">
        {claim.division?.name || claim.division_id?.slice(0, 8) || 'Unknown'}
      </div>
      {isWizard
        ? <WizardStepBadge claim={claim} />
        : <ScoreBadge score={claim.ai_review?.satisfaction_score} />}
    </div>
    <div className="text-xs text-muted-foreground line-clamp-1">
      {claim.policy?.title || claim.policy_id?.slice(0, 8)}
    </div>
    <div className="flex items-center justify-between text-xs">
      <span className="font-bold tabular-nums text-emerald-800">
        ₹{claimedAmount.toLocaleString('en-IN')}
      </span>
      <span className="text-muted-foreground">
        {isWizard
          ? `${(claim.proposed_items || []).length} item${(claim.proposed_items || []).length === 1 ? '' : 's'}`
          : `${(claim.attachments || []).length} doc(s)`}
      </span>
    </div>
    {/* Partial-approval pill — only shown when admin approved less than
        the division claimed. Mirrors the "Approved by MPL" row in the
        detail panel so the haircut is scannable from the kanban. */}
    {partial && (
      <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 text-[10px] gap-1 border border-amber-300">
        Approved ₹{approvedAmount.toLocaleString('en-IN')} of ₹{claimedAmount.toLocaleString('en-IN')}
      </Badge>
    )}
    {claim.sent_back_pending && (
      <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 text-[10px] gap-1 border border-amber-300">
        <RotateCcw className="h-2.5 w-2.5" /> Sent back · awaiting resubmit
      </Badge>
    )}
    {claim.status === 'paid' && (
      (claim.utilization_documents || []).length > 0 ? (
        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px] gap-1">
          <CheckCircle2 className="h-2.5 w-2.5" /> UC submitted ({(claim.utilization_documents || []).length})
        </Badge>
      ) : (
        <Badge variant="outline" className="text-[10px] gap-1 text-amber-700 border-amber-300 bg-amber-50">
          <AlertCircle className="h-2.5 w-2.5" /> UC pending
        </Badge>
      )
    )}
    {/* Legacy "missing documents" hint — suppressed for wizard claims
        (those use the wizard step verdicts instead, surfaced via the
        WizardStepBadge above and the detail panel's 5-step checklist). */}
    {!isWizard && (claim.ai_review?.missing_documents || []).length > 0 && (
      <div className="flex items-start gap-1 text-[11px] text-rose-700 overflow-hidden">
        <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
        <span className="line-clamp-3 break-words leading-snug">
          Missing: {claim.ai_review.missing_documents.map((m) => m.replace(/_/g, ' ')).join(', ')}
        </span>
      </div>
    )}
  </button>
  );
};

const GrantClaims = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const policyFilter = searchParams.get('policy');

  const [claims, setClaims] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [ucTracker, setUcTracker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [activeClaim, setActiveClaim] = useState(null);
  const [acting, setActing] = useState(false);

  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackNote, setSendBackNote] = useState('');

  // Approved-amount editor state. Reset whenever the active claim changes
  // so the input always reflects the persisted value (or empty when none).
  const [approvedAmount, setApprovedAmount] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const previewUrl = previewAttachment && activeClaim
    ? `${API}/admin/grant-claims/${activeClaim.id}/attachments/${previewAttachment.id}/download`
    : null;

  const fetchAll = async () => {
    try {
      const reqs = [
        axios.get(`${API}/admin/grant-claims`, { withCredentials: true }),
        axios.get(`${API}/admin/grant-claims/uc-tracker`, { withCredentials: true }).catch(() => null),
      ];
      if (policyFilter) {
        reqs.push(axios.get(`${API}/grant-policies`, { withCredentials: true }).catch(() => null));
      }
      const [res, ucRes, polRes] = await Promise.all(reqs);
      const all = res.data || [];
      setClaims(policyFilter ? all.filter((c) => c.policy_id === policyFilter) : all);
      if (ucRes?.data) setUcTracker(ucRes.data);
      if (polRes && Array.isArray(polRes.data)) {
        setPolicy(polRes.data.find((p) => p.id === policyFilter) || null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load claims');
    } finally {
      setLoading(false);
    }
  };

  const fetchOne = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/grant-claims/${id}`, { withCredentials: true });
      setActiveClaim(res.data);
      setApprovedAmount(
        res.data.approved_amount != null ? String(res.data.approved_amount) : ''
      );
      setApprovalNote(res.data.approval_note || '');
    } catch (e) {
      toast.error('Failed to load claim');
    }
  };

  const saveApprovedAmount = async () => {
    if (!activeClaim) return;
    setActing(true);
    try {
      await axios.put(
        `${API}/admin/grant-claims/${activeClaim.id}/approved-amount`,
        { approved_amount: Number(approvedAmount), note: approvalNote || null },
        { withCredentials: true },
      );
      toast.success('Approved amount saved — division will see the revised figure');
      await Promise.all([fetchAll(), fetchOne(activeClaim.id)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setActing(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [policyFilter]);
  useEffect(() => { if (activeId) fetchOne(activeId); else setActiveClaim(null); }, [activeId]);

  // Review-on-open: re-run an incremental AI review when an admin opens a claim
  // so prompt changes made in /grant-policies are applied to existing docs (the
  // backend re-judges verdicts stamped under an older prompt). Cheap no-op when
  // nothing is stale; skipped for drafts (the admin endpoint rejects those).
  const onOpenReviewRef = useRef(null);
  useEffect(() => {
    const id = activeClaim?.id;
    if (!id) { onOpenReviewRef.current = null; return; }
    if (activeClaim?.status === 'draft') return;
    if (!(activeClaim?.attachments?.length)) return;
    if (onOpenReviewRef.current === id) return;
    onOpenReviewRef.current = id;
    (async () => {
      try {
        await axios.post(`${API}/admin/grant-claims/${id}/ai-review?only_new=true`, {}, { withCredentials: true });
        const res = await axios.get(`${API}/admin/grant-claims/${id}`, { withCredentials: true });
        setActiveClaim((prev) => (prev && prev.id === id ? res.data : prev));
      } catch { /* keep cached verdicts on failure */ }
    })();
  }, [activeClaim?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const byStatus = useMemo(() => {
    const m = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    for (const c of claims) {
      if (m[c.status]) m[c.status].push(c);
    }
    return m;
  }, [claims]);

  const advance = async () => {
    if (!activeClaim) return;
    const next = NEXT_STATUS[activeClaim.status];
    if (!next) {
      toast.info('Already at the final stage');
      return;
    }
    if (!window.confirm(`Move claim to "${STATUS_LABEL[next]}"?`)) return;
    setActing(true);
    try {
      await axios.put(
        `${API}/admin/grant-claims/${activeClaim.id}/status`,
        { status: next },
        { withCredentials: true },
      );
      toast.success(`Moved to ${STATUS_LABEL[next]}`);
      await Promise.all([fetchAll(), fetchOne(activeClaim.id)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setActing(false);
    }
  };

  const sendBack = async () => {
    if (!activeClaim || !sendBackNote.trim()) {
      toast.error('Please write a note explaining what needs to change');
      return;
    }
    setActing(true);
    try {
      await axios.put(
        `${API}/admin/grant-claims/${activeClaim.id}/status`,
        { send_back: true, note: sendBackNote.trim() },
        { withCredentials: true },
      );
      toast.success('Claim sent back to division');
      setSendBackOpen(false);
      setSendBackNote('');
      await Promise.all([fetchAll(), fetchOne(activeClaim.id)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setActing(false);
    }
  };

  const runAiReview = async () => {
    if (!activeClaim) return;
    setActing(true);
    try {
      const res = await axios.post(
        `${API}/admin/grant-claims/${activeClaim.id}/ai-review`,
        {},
        { withCredentials: true, timeout: 120000 },
      );
      if (res.data?.ai_review) {
        toast.success('AI review complete');
      } else {
        toast.info(res.data?.message || 'AI review ran — no verdict produced. Check server logs.');
      }
      await fetchOne(activeClaim.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI review failed');
    } finally {
      setActing(false);
    }
  };

  const downloadAttachment = async (att) => {
    if (!activeClaim) return;
    try {
      const res = await axios.get(
        `${API}/admin/grant-claims/${activeClaim.id}/attachments/${att.id}/download`,
        { responseType: 'blob', withCredentials: true },
      );
      const blob = new Blob([res.data], { type: att.content_type || res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = att.filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const downloadUcDoc = async (doc) => {
    if (!activeClaim) return;
    try {
      const res = await axios.get(
        `${API}/admin/grant-claims/${activeClaim.id}/utilization-documents/${doc.id}/download`,
        { responseType: 'blob', withCredentials: true },
      );
      const blob = new Blob([res.data], { type: doc.content_type || res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = doc.filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const runUcAiReview = async () => {
    if (!activeClaim) return;
    setActing(true);
    try {
      const res = await axios.post(
        `${API}/admin/grant-claims/${activeClaim.id}/uc-ai-review`,
        {},
        { withCredentials: true, timeout: 120000 },
      );
      if (res.data?.uc_ai_review) {
        toast.success(`UC verified — score ${res.data.uc_ai_review.verification_score}`);
      } else {
        toast.info(res.data?.message || 'UC review attempted — check logs');
      }
      await fetchOne(activeClaim.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'UC AI review failed');
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="grant-claims-page">
      {policyFilter && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/divisions')}
          data-testid="back-to-divisions"
          className="-ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to MPCA Division Portal
        </Button>
      )}
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-3">
          <FileCheck2 className="h-7 w-7 text-accent" />
          {policy ? `Claims · ${policy.title}` : 'Grant Claims'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {policy ? (
            <>
              {policy.fiscal_year} · Eligible ₹{(policy.eligible_amount || 0).toLocaleString('en-IN')} per division.
              Review submitted claims, AI-assist your verdict, and move them through the pipeline.
            </>
          ) : (
            <>Review claims submitted by division secretaries, AI-assist your verdict, and move them through the pipeline.</>
          )}
        </p>
      </div>

      {/* UC Tracker — only meaningful in the global (unfiltered) view */}
      {!policyFilter && ucTracker?.summary?.total_paid > 0 && (
        <Card data-testid="uc-tracker-card" className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm font-heading font-semibold">
                <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                Utilisation Certificate Tracker
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span className="tabular-nums"><strong className="text-foreground">{ucTracker.summary.total_paid}</strong> paid</span>
                <span className="text-emerald-800 tabular-nums"><strong>{ucTracker.summary.uc_submitted}</strong> UC submitted</span>
                <span className="text-amber-800 tabular-nums"><strong>{ucTracker.summary.uc_pending}</strong> UC pending</span>
              </div>
            </div>
            {ucTracker.summary.uc_pending > 0 && (
              <div className="mt-2 pt-2 border-t border-emerald-200">
                <div className="text-xs text-muted-foreground mb-1.5">Pending divisions:</div>
                <div className="flex flex-wrap gap-1.5">
                  {(ucTracker.rows || []).filter((r) => !r.uc_submitted).slice(0, 12).map((r) => (
                    <Badge
                      key={r.claim_id}
                      variant="outline"
                      className="text-[11px] bg-white cursor-pointer hover:bg-amber-100 border-amber-300"
                      onClick={() => setActiveId(r.claim_id)}
                    >
                      {r.division?.name || r.division?.id?.slice(0, 8) || '—'}
                    </Badge>
                  ))}
                  {ucTracker.rows.filter((r) => !r.uc_submitted).length > 12 && (
                    <Badge variant="outline" className="text-[11px]">
                      +{ucTracker.rows.filter((r) => !r.uc_submitted).length - 12} more
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading claims…
        </div>
      ) : claims.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Inbox className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <div className="text-sm text-muted-foreground">
              No claims yet. Once a division secretary submits, you'll see their claim here.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {COLUMNS.map((col) => (
            <div
              key={col.key}
              className={`rounded-xl border-2 ${col.tone} p-3 space-y-2 min-h-[300px]`}
              data-testid={`kanban-col-${col.key}`}
            >
              <div className="flex items-center justify-between sticky top-0 z-10 pb-1.5 mb-1">
                <div className="font-heading font-semibold text-sm">{col.label}</div>
                <div className="flex items-center gap-1">
                  {col.key === 'in_process' && byStatus[col.key].filter((c) => c.sent_back_pending).length > 0 && (
                    <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 text-[10px] border border-amber-300">
                      {byStatus[col.key].filter((c) => c.sent_back_pending).length} sent back
                    </Badge>
                  )}
                  <Badge variant="secondary" className="text-xs">{byStatus[col.key].length}</Badge>
                </div>
              </div>
              {byStatus[col.key].length === 0 ? (
                <div className="text-xs text-muted-foreground py-4 text-center">No claims</div>
              ) : (
                byStatus[col.key].map((c) => (
                  <ClaimCard key={c.id} claim={c} onClick={() => setActiveId(c.id)} />
                ))
              )}
            </div>
          ))}
        </div>
      )}

      {/* Side-sheet for claim detail */}
      <Sheet open={!!activeId} onOpenChange={(o) => !o && setActiveId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {!activeClaim ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : activeClaim.wizard_state != null ? (
            <AdminWizardClaimDetail
              claim={activeClaim}
              onAction={async () => { await Promise.all([fetchAll(), fetchOne(activeClaim.id)]); }}
            />
          ) : (
            <>
              <SheetHeader className="border-b pb-4 mb-4">
                <SheetTitle className="font-heading flex items-start justify-between gap-3">
                  <span>
                    {activeClaim.division?.name || 'Claim'}
                    <span className="block text-sm font-normal text-muted-foreground mt-1">
                      {activeClaim.policy?.title} · {activeClaim.fiscal_year}
                    </span>
                  </span>
                  <Badge className="text-sm px-3 py-1">
                    {STATUS_LABEL[activeClaim.status]}
                  </Badge>
                </SheetTitle>
              </SheetHeader>

              <div className="space-y-5">
                {/* Top stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Claim Amount</div>
                    <div className="font-bold tabular-nums text-emerald-800 mt-0.5">
                      ₹{(activeClaim.claim_amount || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Eligible</div>
                    <div className="font-semibold tabular-nums mt-0.5">
                      ₹{(activeClaim.policy?.eligible_amount || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Submitted</div>
                    <div className="text-sm mt-0.5">
                      {activeClaim.submitted_at ? new Date(activeClaim.submitted_at).toLocaleDateString('en-IN') : '—'}
                    </div>
                  </div>
                </div>

                {/* Approved amount editor (admin sets after document review) */}
                <Card data-testid="approved-amount-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-heading flex items-center gap-2">
                      <IndianRupee className="h-4 w-4 text-emerald-700" />
                      Approved Amount
                      {activeClaim.approved_amount != null && (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px] ml-1">
                          ₹{(activeClaim.approved_amount || 0).toLocaleString('en-IN')}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 items-end">
                      <div>
                        <Label className="text-xs">Approved (₹)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          max={activeClaim.claim_amount}
                          value={approvedAmount}
                          onChange={(e) => setApprovedAmount(e.target.value)}
                          placeholder={String(activeClaim.claim_amount)}
                          data-testid="approved-amount-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Note (optional)</Label>
                        <Input
                          value={approvalNote}
                          onChange={(e) => setApprovalNote(e.target.value)}
                          placeholder="e.g. Quotation total reduced"
                          data-testid="approval-note-input"
                        />
                      </div>
                    </div>
                    {activeClaim.ai_review?.recommended_amount != null
                      && Number(activeClaim.ai_review.recommended_amount) !== Number(activeClaim.claim_amount) && (
                      <div className="text-xs flex items-center gap-1.5 text-amber-800">
                        <Sparkles className="h-3 w-3" />
                        AI suggests <strong>₹{Number(activeClaim.ai_review.recommended_amount).toLocaleString('en-IN')}</strong> based on the documents.
                        <button
                          type="button"
                          className="underline underline-offset-2 hover:text-amber-900"
                          onClick={() => setApprovedAmount(String(activeClaim.ai_review.recommended_amount))}
                          data-testid="use-ai-recommendation-button"
                        >
                          Use this
                        </button>
                      </div>
                    )}
                    <Button
                      size="sm"
                      onClick={saveApprovedAmount}
                      disabled={
                        acting
                        || approvedAmount === '' 
                        || Number(approvedAmount) > Number(activeClaim.claim_amount)
                        || Number(approvedAmount) < 0
                      }
                      data-testid="save-approved-amount-button"
                    >
                      {acting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                      Save approved amount
                    </Button>
                  </CardContent>
                </Card>

                {/* AI review */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-heading flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-amber-600" />
                        AI Review
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={runAiReview}
                        disabled={acting}
                        data-testid="run-ai-review-button"
                      >
                        {acting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                        {activeClaim.ai_review ? 'Re-run' : 'Run AI Review'}
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    {!activeClaim.ai_review ? (
                      <div className="text-xs text-muted-foreground italic">
                        No AI verdict yet. Click <strong>Run AI Review</strong> above — the model will inspect every attached page and post a satisfaction score, missing-doc list and per-doc summary.
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <ScoreBadge score={activeClaim.ai_review.satisfaction_score} />
                          {(activeClaim.ai_review.missing_documents || []).length > 0 && (
                            <Badge variant="destructive" className="text-[10px]">
                              Missing: {activeClaim.ai_review.missing_documents.join(', ')}
                            </Badge>
                          )}
                        </div>
                        <p className="whitespace-pre-wrap text-sm">
                          {activeClaim.ai_review.comments}
                        </p>
                        {(activeClaim.ai_review.per_doc || []).length > 0 && (
                          <div className="space-y-1.5 pt-1">
                            {activeClaim.ai_review.per_doc.map((d, i) => (
                              <div key={i} className="text-xs border-l-2 border-emerald-200 pl-2">
                                <div className="font-medium">{d.filename} <span className="text-muted-foreground">({d.doc_type})</span></div>
                                <div className="text-muted-foreground">{d.summary}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Notes from secretary */}
                {activeClaim.notes && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-heading">Notes from secretary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm whitespace-pre-wrap">{activeClaim.notes}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Attachments — unified checklist per required slug, with
                    inline preview + per-doc AI verdict. */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-heading flex items-center justify-between gap-2 flex-wrap">
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Documents ({(activeClaim.attachments || []).length})
                      </span>
                      {activeClaim.viewing_pre_revision_snapshot && (
                        <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">
                          Showing pre-revision snapshot — division is reworking
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RequiredDocsChecklist
                      claim={activeClaim}
                      onPreview={(att) => setPreviewAttachment(att)}
                    />
                  </CardContent>
                </Card>

                {/* Utilisation Certificate documents (visible only when the
                    secretary has uploaded any post-payout artefacts). */}
                {(activeClaim.utilization_documents || []).length > 0 && (
                  <Card data-testid="admin-uc-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-heading flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-emerald-700" />
                          Utilisation Certificate
                          <Badge variant="secondary" className="text-xs ml-1">
                            {(activeClaim.utilization_documents || []).length}
                          </Badge>
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={runUcAiReview}
                          disabled={acting}
                          data-testid="run-uc-ai-button"
                        >
                          {acting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                          {activeClaim.uc_ai_review ? 'Re-verify UC' : 'Verify UC with AI'}
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {(activeClaim.utilization_documents || []).map((d) => (
                        <div key={d.id} className="flex items-center justify-between gap-2 text-sm py-1.5 border-b last:border-0">
                          <div className="min-w-0 flex-1">
                            <Badge variant="outline" className="text-[10px] mr-2">{d.doc_type}</Badge>
                            <span className="font-mono text-xs truncate">{d.filename}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {d.size_bytes ? `${Math.round(d.size_bytes / 1024)} KB` : ''}
                            </span>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => downloadUcDoc(d)}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {activeClaim.uc_ai_review && (
                        <div className="mt-3 rounded-md border bg-amber-50 border-amber-200 p-3 text-xs space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-amber-900 flex items-center gap-1.5">
                              <Sparkles className="h-3 w-3" /> AI Audit Verdict
                            </span>
                            <Badge
                              className={
                                activeClaim.uc_ai_review.verdict === 'satisfactory'
                                  ? 'bg-emerald-600 hover:bg-emerald-600'
                                  : activeClaim.uc_ai_review.verdict === 'non_compliant'
                                  ? 'bg-rose-600 hover:bg-rose-600'
                                  : 'bg-amber-600 hover:bg-amber-600'
                              }
                            >
                              {activeClaim.uc_ai_review.verdict} · {activeClaim.uc_ai_review.verification_score}/100
                            </Badge>
                          </div>
                          {activeClaim.uc_ai_review.comments && (
                            <p className="whitespace-pre-wrap text-amber-900/90">{activeClaim.uc_ai_review.comments}</p>
                          )}
                          {(activeClaim.uc_ai_review.concerns || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-1">
                              {activeClaim.uc_ai_review.concerns.map((c, i) => (
                                <Badge key={i} variant="destructive" className="text-[10px]">{c}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Status history */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-heading">Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ol className="space-y-2.5 text-sm">
                      {(activeClaim.status_history || []).map((h, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <div className={`h-2 w-2 rounded-full mt-1.5 flex-shrink-0 ${h.sent_back ? 'bg-amber-600' : 'bg-emerald-600'}`} />
                          <div className="flex-1">
                            <div className="font-medium">
                              {STATUS_LABEL[h.status] || h.status}
                              {h.note && <span className="text-muted-foreground font-normal"> · {h.note}</span>}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {h.at ? new Date(h.at).toLocaleString('en-IN') : ''} · {h.by || ''}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>

                {/* Action buttons */}
                <div className="sticky bottom-0 -mx-6 bg-white border-t pt-4 px-6 pb-2 flex flex-wrap gap-2 justify-end">
                  {activeClaim.status !== 'paid' && (
                    <Button
                      variant="outline"
                      onClick={() => setSendBackOpen(true)}
                      disabled={acting}
                      data-testid="send-back-button"
                    >
                      <RotateCcw className="h-4 w-4 mr-1.5" />
                      Send back
                    </Button>
                  )}
                  {NEXT_STATUS[activeClaim.status] && (
                    <Button
                      onClick={advance}
                      disabled={acting}
                      data-testid="advance-status-button"
                    >
                      {activeClaim.status === 'approved' ? (
                        <IndianRupee className="h-4 w-4 mr-1.5" />
                      ) : activeClaim.status === 'payment_initiated' ? (
                        <CheckCircle2 className="h-4 w-4 mr-1.5" />
                      ) : (
                        <ArrowRight className="h-4 w-4 mr-1.5" />
                      )}
                      Move to {STATUS_LABEL[NEXT_STATUS[activeClaim.status]]}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Send-back dialog */}
      <Dialog open={sendBackOpen} onOpenChange={setSendBackOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send claim back to division</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The secretary will get an email with this note and the claim will return to <strong>In Process</strong>.
            </p>
            <Textarea
              rows={4}
              placeholder="What needs to change? e.g. 'Invoice total doesn't match claim amount — please re-upload with the correct figure.'"
              value={sendBackNote}
              onChange={(e) => setSendBackNote(e.target.value)}
              data-testid="send-back-note"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSendBackOpen(false)}>Cancel</Button>
              <Button onClick={sendBack} disabled={acting || !sendBackNote.trim()} data-testid="confirm-send-back">
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Send back
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DocumentPreviewModal
        open={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        title={previewAttachment?.filename}
        url={previewUrl}
        contentType={previewAttachment?.content_type}
      />
    </div>
  );
};

export default GrantClaims;
